import { Observable } from "rxjs";
import { List } from "immutable";
import { formatMessage } from "lib/globalize-util";
import siteConfig from "site-config";
import { ajax } from "lib/observable";
import { COLLECTIONS, MUTATE_OPERATIONS_SENT } from "constants/action-types";
import { ALLOCATION_DIMENSION_ID_PREFIX } from "constants/product-collection";
import { buildMutateOperation, AllocationDimensionOperationKey } from "helpers/allocation-dimension";
import { IOperation, OperationType } from "actions/mutate";
import { add as snackbarAdd } from "actions/snackbars";
import { getAndIncrementRequestId } from "./helpers";

const allocationDimensionValueOperationMap = {
    [OperationType.Insert]: AllocationDimensionOperationKey.InsertDimensionValue,
    [OperationType.Set]: AllocationDimensionOperationKey.SetDimensionValue,
    [OperationType.Move]: AllocationDimensionOperationKey.MoveDimensionValue,
};
const normalizeToAllocationDimensionValueOperation = (operation: IOperation) => {
    if (operation.childKey === "dimension_values") {
        // Mutating allocation dimension's `dimension_values`
        const mappedOperationType = allocationDimensionValueOperationMap[operation.operation];

        let operationValue;
        switch (mappedOperationType) {
            case AllocationDimensionOperationKey.InsertDimensionValue:
            case AllocationDimensionOperationKey.SetDimensionValue:
                operationValue = {
                    index: operation.index,
                    value: operation.value.get("struct_value").toJS(),
                };
                break;
            case AllocationDimensionOperationKey.MoveDimensionValue:
                operationValue = {
                    index: operation.index,
                    destination_index: operation.destination_index,
                };
                break;
            default:
                return undefined;
        }

        return buildMutateOperation(mappedOperationType, operationValue);
    } else if (operation.childKey === "field_map") {
        // Mutating allocation dimension's `field_map`, these fields include `name`, `active` and `attributes`.

        switch (operation.key) {
            case "attributes":
                return buildMutateOperation(AllocationDimensionOperationKey.SetAttributes, {
                    keys: operation.value.getIn(["struct_value", "keys"]).toJS(),
                });
            default:
                return undefined;
        }
    }
};

export const syncCollectionAllocationDimensionEpic = (action$, store$) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.ALLOCATION_DIMENSION.SYNC.START)
        // Dont proceed if there is something sending or there is nothing to send
        .filter(({ payload: { objectId } }) => {
            const { mutate } = store$.getState();
            const { sentMutations, uncommittedMutations } = mutate;
            const sentMutation = sentMutations.get(objectId);
            const uncommittedMutation = uncommittedMutations.get(objectId) || List();

            return !sentMutation && !!uncommittedMutation.size;
        })
        .map(({ payload: { objectId } }: { payload: { objectId: string } }) => {
            const { collection, mutate } = store$.getState();
            const { committedHEADs, uncommittedMutations } = mutate;
            const { sessionId, merchantId } = collection;

            const committedHEAD = committedHEADs.get(objectId);

            const operations = [];
            uncommittedMutations.get(objectId).forEach((mutation) => {
                mutation.operations.forEach((operation) => {
                    const normalizedOperation = normalizeToAllocationDimensionValueOperation(operation);
                    if (normalizedOperation) {
                        operations.push(normalizedOperation);
                    }
                });
            });
            const requestId = getAndIncrementRequestId(objectId);
            const revision = parseInt(committedHEAD.revision, 10) + 1;
            const dataToSave = {
                req_id: requestId,
                operations,
                revision,
            };

            store$.dispatch({
                type: MUTATE_OPERATIONS_SENT,
                payload: {
                    requestId,
                    objectId,
                    revision,
                    objectType: committedHEAD.object_type,
                },
            });

            // Allocation dimension id is prepended by "allocation_dimension_"
            const allocationDimensionId = objectId.replace(ALLOCATION_DIMENSION_ID_PREFIX, "");

            return {
                dataToSave,
                sessionId,
                requestId,
                collectionId: committedHEAD.document_id,
                allocationDimensionId,
                merchantId,
            };
        })
        // Make it a queue, wait for response before continuing
        .concatMap(({ dataToSave, collectionId, requestId, sessionId, allocationDimensionId, merchantId }) => {
            const url = `/v1/collections/${collectionId}/allocation_dimensions/${allocationDimensionId}?sid=${sessionId}&req_id=${requestId}`;
            return ajax({
                body: { ...dataToSave, merchant_id: merchantId },
                method: "POST",
                url: siteConfig.goAPI + url,
            })
                .map(() => ({
                    payload: { collectionId },
                    type: COLLECTIONS.COLLECTION.ALLOCATION_DIMENSION.SYNC.COMPLETED,
                }))
                .catch(() => {
                    return Observable.merge(
                        Observable.of(
                            snackbarAdd({
                                message: formatMessage("There was an error while saving. Please try again."),
                            }),
                        ),
                    );
                });
        });
